import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  Chip,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import useDebounce from "../../hooks/useDebounce";
import { User } from "../../types/users";
import { Company } from "../../types/company";
import { useGetCompanyByIdUserQuery } from "../../services/company.service";
import dayjs from "dayjs";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";


const CompanyAccountPanel = ({ data: dataCompany }: { data?: Company; }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);
  const { data, isLoading, isFetching } = useGetCompanyByIdUserQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
    company: dataCompany?.id || -1,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "first_name",
        headerName: isMobile ? "Name" : "Manager's Name",
        flex: isMobile ? 0 : 1,
        minWidth: isMobile ? 150 : 200,
        sortingOrder: ["desc", "asc", null],
        filterable: true,
        renderCell: (params: GridRenderCellParams<undefined, User>) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
            {params.row.full_name}
          </Typography>
        ),
      },
      {
        field: "email",
        headerName: "Email",
        headerAlign: "center",
        minWidth: isMobile ? 150 : 200,
        flex: isMobile ? 0 : undefined,
        sortable: true,
        filterable: true,
        renderCell: (params: GridRenderCellParams<undefined, User>) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "phone_number",
        headerName: "Phone",
        headerAlign: "center",
        minWidth: isMobile ? 100 : 150,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams<undefined, User>) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
            {params.value || "n/a"}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        headerAlign: "center",
        minWidth: isMobile ? 80 : 100,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams<any, any>) => {
          const status = params.row.status;
          return (
            <Chip
              label={status?.toUpperCase() || "N/A"}
              size="small"
              color={status == "active" ? "success" : undefined}
              sx={{
                fontSize: { xs: 10, sm: 12 },
                height: { xs: 24, sm: 28 },
              }}
            />
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created",
        headerAlign: "center",
        minWidth: isMobile ? 120 : 200,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams<undefined, User>) =>
          `${dayjs(params.row.created_at).format("MMM DD, YYYY")}`,
        renderCell: (params: GridRenderCellParams<undefined, User>) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
            {params.value}
          </Typography>
        ),
      },
    ],
    [isMobile]
  );

  return (
    <CardBase
      title="Managers"
      useGlassCard={false}
      headerActions={
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <StyledTextField
            size="small"
            label="Enter to search.."
            value={query || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            sx={{
              minWidth: { xs: "100%", sm: 250 },
              width: { xs: "100%", sm: "auto" },
            }}
          />
          {!!query && (
            <Button
              variant="text"
              onClick={() => setQuery("")}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "auto" },
                color: "#F4A259",
                "&:hover": {
                  backgroundColor: "rgba(244, 162, 89, 0.08)",
                },
              }}
            >
              Reset
            </Button>
          )}
        </Stack>
      }
      sx={{
        height: { xs: "70vh", sm: "65vh" },
        p: 0,
      }}
    >
      <GlassDataGrid
        rows={rows || []}
        columns={columns}
        paginationMode="server"
        rowCount={rowCount}
        page={page}
        onPageChange={(newPage: number) => setPage(newPage)}
        pageSize={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageSizeChange={(newSize: number) => setPageSize(newSize)}
        sortModel={sortModel}
        sortingMode="server"
        onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
        loading={isLoading || isFetching}
        components={{
          LoadingOverlay: OrangeLinearProgress,
          NoResultsOverlay: NoResultsOverlay,
        }}
        disableSelectionOnClick
        disableColumnFilter
        height={isMobile ? "calc(70vh - 100px)" : "calc(65vh - 80px)"}
        sx={{
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "transparent",
            borderTop: "none",
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#F4A259",
              fontWeight: 600,
              fontSize: { xs: "0.875rem", sm: "0.95rem" },
            },
            "& .MuiDataGrid-columnHeader": {
              color: "#F4A259",
              "& .MuiDataGrid-iconButtonContainer": {
                color: "#F4A259",
              },
              "& .MuiDataGrid-sortIcon": {
                color: "#F4A259",
              },
            },
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "rgba(244, 162, 89, 0.05)",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "auto",
          },
          "& .MuiDataGrid-main": {
            overflowX: "auto",
          },
        }}
      />
    </CardBase>
  );
};
export default CompanyAccountPanel;
